import React, { useState } from 'react';
import styled from 'styled-components';
import listService from 'ListService';
import util from 'util/Util.js';
import ToastNotification, { toast } from '../Common/ToastNotification';
import languages from 'Languages.js';
import ConfirmModal from '../Common/ConfirmModal';
import { Button } from '../Common/StyledComponents';

const Container = styled.div`
    padding: 15px;
  font-size: 0.9em;
  color: #777;
  box-sizing: border-box;
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
`;


function ExportPanel({ onSave, setLanguages, setCurrentTranslations, setIsDirty, setIsLoading, setLoadingText}) {

    const [modalOpen, setModalOpen] = useState(false);

    const exportMultilanguageAsJson = async () => {
        try {
            let saveSuccessful = true;
            if (onSave) {
                saveSuccessful = await onSave({ keepLoading: true });
            }

            if (saveSuccessful) {
                setLoadingText('Exporting...');
                
                // A delay to let React render the text change
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                const obj = listService.getObject("aForm");
                const fileName = obj.model.get("name").replace(" ", "_") + "_multilanguage.json";
                const json = JSON.stringify(listService.multilanguage, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

            } else {
                return;
            }

        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setIsLoading(false);
            setLoadingText('');
        }
    };

    const handleImport = () => {
        setModalOpen(true);
    };

    const importMultilanguageFromJson = () => {

        setModalOpen(false);

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            setLoadingText('Importing...');
            setIsLoading(true);

            //Delay since the import might be quick so the loading don't just flickers
            await new Promise(resolve => setTimeout(resolve, 1000));

            try {
                const text = await file.text();
                const json = JSON.parse(text);

                // Sanitize the language names
                const sanitizedLanguages = json.languages.map(lang => ({
                    ...lang,
                    name: lang.name ? lang.name.replace(/<[^>]*>/g, '') : ''
                }));

                // Recursively sanitize the translation values (allow basic HTML)
                const deepSanitize = (obj) => {
                    for (const key in obj) {
                        if (typeof obj[key] === 'string') {
                            // Do not sanitize the read-only default value
                            if (key !== 'default-readonly') {
                                obj[key] = util.sanitizeHTML(obj[key], true, obj[key], true);
                            }
                        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                            deepSanitize(obj[key]);
                        }
                    }
                    return obj;
                };

                const sanitizedTranslations = deepSanitize(json.translations);

                if (!json.languages) {
                    throw new Error('The \'languages\' section is missing. Please check your JSON file format. You can use the Export option to download a valid structure.');
                }

                if (!json.translations) {
                    throw new Error('The \'translations\' section is missing. Please check your JSON file format. You can use the Export option to download a valid structure.');
                }

                const seenCodes = new Set();
                const validCodes = languages.getLanguagesList().map(l => l.code);

                for (const lang of json.languages) {
                    if (!lang.code) {
                        throw new Error(`Language entry is incomplete: missing \'code\'.`);
                    }
                    if (seenCodes.has(lang.code)) {
                        throw new Error(`Duplicate language code: ${lang.code}`);
                    }
                    if (!validCodes.includes(lang.code)) {
                        throw new Error(`Language code ${lang.code} is not recognized.`);
                    }
                    seenCodes.add(lang.code);
                }

                const mergedTranslations = { ...listService.multilanguage.translations, ...json.translations };

                const merged = {
                    languages: sanitizedLanguages,
                    translations: sanitizedTranslations
                };

                setLanguages(merged.languages);
                setCurrentTranslations(merged.translations);
                listService.multilanguage = merged;
                setIsDirty(true);

                toast.show({
                    type: 'success',
                    message: 'Import successful.',
                    position: 'right'
                });
            } catch (err) {
                toast.show({
                    type: 'error',
                    message: `Import failed: ${err.message}`,
                    position: 'right',
                    duration: 5000
                });
            } finally {
                setIsLoading(false);
                setLoadingText('');
            }
        };
        input.click();
    };

    return (
        <>
            <Container>
                <Button variant="orange" onClick={handleImport}>Import JSON</Button>
                <Button variant="orange" onClick={exportMultilanguageAsJson}>Export JSON</Button>
            </Container>
            {modalOpen && (
                <ConfirmModal
                    title="Import JSON"
                    onClose={() => setModalOpen(false)}
                    buttons={[
                        {
                            label: 'Cancel',
                            variant: 'default',
                            onClick: () => setModalOpen(false),
                            icon: ''
                        },
                        {
                            label: 'Yes, Import',
                            variant: 'orange',
                            onClick: importMultilanguageFromJson,
                            icon: ''
                        }
                    ]}
                >
                    <div>
                        Importing this file will merge its contents with your current configuration. This will overwrite existing data.
                        <br /><br />Do you want to proceed?
                    </div>

                </ConfirmModal>
            )}
        </>
    );
}

export default ExportPanel;
